import React, { useState, createContext, useEffect, useContext } from 'react';
import { HttpRequestContext } from '../Blocks/HttpRequestProvider';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';

export const UserContext = createContext(null);

// List of routes that don't require authentication
const publicRoutes = ['/login', '/onboarding', '/form', '/leonidas'];

// Function to check if a path is a public AI agent route
const isAIAgentRoute = (pathname) => {
  return pathname.startsWith('/ai-agent/');
};

export function UserProvider({ children }) {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true); 
    const { sendRequest } = useContext(HttpRequestContext);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const fetchUserInformation = async () => {
            try {
                const userData = await sendRequest({
                    apiPath: '/user',
                    type: 'GET',
                });
                if (userData) {
                    setUser(userData);
                } else {
                    throw new Error("No user data returned from API.");
                }
            } catch (error) {
                console.error("Error fetching user data:", error);
                if (!publicRoutes.includes(location.pathname) && !isAIAgentRoute(location.pathname)) {
                    navigate('/login');
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchUserInformation();
    }, [sendRequest, navigate, location.pathname]); 

    const logout = async () => {
        console.log("Logging out...");
        try {
            await sendRequest({
                apiPath: '/logout',
                type: 'POST',
            });
            setUser(null);
            navigate('/login');
        } catch (error) {
            console.error("Logout failed:", error);
        }
    };

    // If still loading, don't render anything yet
    if (isLoading) {
        return null; // Or a loading spinner if preferred
    }

    // If logged in and on login page, redirect to dashboard
    if (user && location.pathname === '/login') {
        return <Navigate to="/" replace />;
    }

    // If not logged in and trying to access a protected route, redirect to login
    if (!user && !publicRoutes.includes(location.pathname) && !isAIAgentRoute(location.pathname)) {
        return <Navigate to="/login" replace />;
    }

    return (
        <UserContext.Provider value={{ user, setUser, logout, isLoading }}>
            {children}
        </UserContext.Provider>
    );
}

export default UserProvider;
